// src/routes/Routes.js
/**
 * Main Application Router
 * 
 * Handles top-level routing and authentication
 * Nested routes are managed by individual module routers
 * 
 * ✅ UPDATED: Added MonitoringRoutes for /monitoring/* paths
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Pages
import LoginPage from '../pages/login/LoginPage';
import Layout from '../components/layout/Layout';

// ============================================================================
// NESTED ROUTERS - Module-specific routing
// ============================================================================
import UserManagementRoutes from './UserManagementRoutes';
import CustomerRoutes from './CustomerRoutes';
import MonitoringRoutes from './MonitoringRoutes'; // ✅ NEW: Monitoring module
import MasterDataRoutes from './MasterDataRoutes';

// TODO: Import other module routers as they are created
// import ProjectRoutes from './ProjectRoutes';
// import SPKRoutes from './SPKRoutes';
// import FOSegmentRoutes from './FOSegmentRoutes';
// import BudgetRoutes from './BudgetRoutes';
// import PlanningRoutes from './PlanningRoutes';

/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication
 * Redirects to login if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  
  // Check authentication - supports both session and local storage
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true' || 
                         localStorage.getItem('isAuthenticated') === 'true';
  
  if (!token && !isAuthenticated) {
    // Redirect to login, save the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};

/**
 * Main App Routes
 */
const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* ============================================ */}
        {/* PUBLIC ROUTES - No authentication required */}
        {/* ============================================ */}
        
        <Route path="/login" element={<LoginPage />} />

        {/* ============================================ */}
        {/* PROTECTED ROUTES - Authentication required */}
        {/* ============================================ */}
        
        <Route 
          path="/*" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          } 
        >
          {/* ============================================ */}
          {/* MODULE ROUTES - Nested routers per module */}
          {/* ============================================ */}
          
          {/* 
            USER MANAGEMENT MODULE
            Handles: /usermanagement/*
            Router: UserManagementRoutes.jsx
          */}
          <Route 
            path="usermanagement/*" 
            element={<UserManagementRoutes />} 
          />

          {/* 
            CUSTOMER MODULE
            Handles: /customers/*
            Router: CustomerRoutes.jsx
          */}
          {/*<Route 
            path="customers/*" 
            element={<CustomerRoutes />} 
          /> */}

          {/* 
            MONITORING MODULE ✅ NEW
            Handles: /monitoring/*
            Router: MonitoringRoutes.jsx
            
            Sub-routes:
            - /monitoring/submissions     → Field Submissions (approval workflow)
            - /monitoring/progress        → Progress monitoring (planned)
            - /monitoring/issues          → Issues tracking (planned)
            - /monitoring/rag             → RAG status (planned)
            - /monitoring/budget          → Budget realization (planned)
          */}
          <Route 
            path="monitoring/*" 
            element={<MonitoringRoutes />} 
          />

          {/* ============================================ */}
          {/* TODO: Add other module routes here */}
          {/* ============================================ */}
          
          {/* 
            Pattern for adding new modules:
            
            <Route 
              path="modulename/*" 
              element={<ModuleRoutes />} 
            />
          */}

          {/* Example for future modules: */}
          
          {/* 
          MASTER DATA MODULE */}
          <Route 
            path="masterdata/*" 
            element={<MasterDataRoutes />} 
          />
          {/**/}
          
          {/* 
          PROJECT MODULE
          <Route 
            path="projects/*" 
            element={<ProjectRoutes />} 
          />
          */}
          
          {/* 
          SPK MODULE
          <Route 
            path="spk/*" 
            element={<SPKRoutes />} 
          />
          */}
          
          {/* 
          FO SEGMENT MODULE
          <Route 
            path="fosegments/*" 
            element={<FOSegmentRoutes />} 
          />
          */}
          
          {/* 
          TOWER MODULE
          <Route 
            path="towers/*" 
            element={<TowerRoutes />} 
          />
          */}
          
          {/* 
          ITEM MODULE
          <Route 
            path="items/*" 
            element={<ItemRoutes />} 
          />
          */}
          
          {/* 
          BUDGET MODULE
          <Route 
            path="budget/*" 
            element={<BudgetRoutes />} 
          />
          */}
          
          {/* 
          PLANNING MODULE
          <Route 
            path="planning/*" 
            element={<PlanningRoutes />} 
          />
          */}

        </Route>
        
        {/* ============================================ */}
        {/* DEFAULT REDIRECT */}
        {/* ============================================ */}
        
        {/* Redirect root to dashboard */}
        <Route 
          path="/" 
          element={<Navigate to="/dashboard" replace />} 
        />

        {/* Catch-all - redirect to dashboard */}
        <Route 
          path="*" 
          element={<Navigate to="/dashboard" replace />} 
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;